import { timeClientAsync } from './timing';

const TAB_BRAND = 'Meridian View AI';

export interface AuthUserSummary {
  whopUserId: string;
  email: string | null;
}

interface AuthMeDataBase {
  isAdmin?: boolean;
  checkoutUrl: string | null;
}

export type AuthMeData =
  | (AuthMeDataBase & {
      authenticated: false;
      hasAccess: false;
      accessLevel: null;
      user: null;
    })
  | (AuthMeDataBase & {
      authenticated: true;
      hasAccess: boolean;
      accessLevel: string;
      user: AuthUserSummary;
    });

interface AuthApiResponse {
  success: boolean;
  data?: AuthMeData;
  error?: string;
}

export interface AuthGateState {
  status: 'loading' | 'ready' | 'error';
  data: AuthMeData | null;
  error: string | null;
}

export type AuthRevalidationMode = 'background' | 'loading';
export type AuthRevalidationFn = (mode?: AuthRevalidationMode) => Promise<AuthGateState>;

export function createLoadingAuthGateState(): AuthGateState {
  return {
    status: 'loading',
    data: null,
    error: null,
  };
}

function createReadyAuthGateState(data: AuthMeData): AuthGateState {
  return {
    status: 'ready',
    data,
    error: null,
  };
}

function createErrorAuthGateState(error: string): AuthGateState {
  return {
    status: 'error',
    data: null,
    error,
  };
}

async function fetchAuthState(): Promise<AuthMeData> {
  return timeClientAsync('ui/auth-me-fetch', async () => {
    const response = await fetch('/api/auth/me');
    const json = (await response.json()) as AuthApiResponse;

    if (!response.ok || !json.success || json.data == null) {
      throw new Error(json.error ?? 'Failed to load Whop session');
    }

    if (json.data.authenticated && json.data.user == null) {
      throw new Error('Authenticated Whop session is missing user identity');
    }

    return json.data;
  });
}

export async function resolveAuthGateState(): Promise<AuthGateState> {
  try {
    const data = await fetchAuthState();
    return createReadyAuthGateState(data);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to load Whop session';
    console.error('[auth] failed to load auth state', err);
    return createErrorAuthGateState(message);
  }
}

export function beginWhopLogin() {
  if (typeof window === 'undefined') {
    return;
  }

  window.location.assign('/api/auth/login');
}

export function logoutOfWhopSession() {
  if (typeof window === 'undefined') {
    return;
  }

  void (async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
      });
    } finally {
      window.location.assign('/');
    }
  })();
}

export function readAuthRedirectError(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const authError = new URLSearchParams(window.location.search).get('authError');
  if (authError == null || authError === '') {
    return null;
  }

  if (authError === 'whop_login_failed') {
    return 'Whop sign-in failed. Refresh and try again, or open MeridianView AI from your Whop app page.';
  }

  return 'Whop sign-in could not be completed.';
}

export function getAuthScreenTitle(authGateState: AuthGateState): string | null {
  if (authGateState.status === 'loading') {
    return `${TAB_BRAND} | Checking Access`;
  }

  if (authGateState.status === 'error' || authGateState.data == null) {
    return `${TAB_BRAND} | Auth Error`;
  }

  if (!authGateState.data.authenticated) {
    return `${TAB_BRAND} | Sign In`;
  }

  if (!authGateState.data.hasAccess) {
    return `${TAB_BRAND} | Access Required`;
  }

  return null;
}

export function isAuthStatus(status: number): boolean {
  return status === 401 || status === 403;
}
